import React from 'react';
import { Shield, Lock, Eye, User, FileText, Phone, CheckCircle } from 'lucide-react';
import HimnarioLogo from '../../src/images/himnario-logo.png'; // Ensure this path is correct

// Simple background with minimal elements
const Background = () => (
    <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gray-50" />
    </div>
);

// Clean header component
const Header = () => (
    <header className="text-center mb-16">
        <div className="w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-6">
            <img
                src={HimnarioLogo}
                alt="Goland.dev Logo"
            />
        </div>

        <h1 className="text-4xl font-light text-gray-900 mb-2">
            Privacy Policy
        </h1>

        <div className="w-12 h-px bg-gray-300 mx-auto mb-6" />

        <p className="text-sm text-gray-600">
            Last Updated: October 18, 2024
        </p>
    </header>
);

// Minimal section component
const Section = ({ icon: Icon, title, children, className = "" }) => (
    <section className={`mb-12 ${className}`}>
        <div className="flex items-center space-x-3 mb-6">
            <Icon className="w-5 h-5 text-gray-700" />
            <h2 className="text-xl font-medium text-gray-900">{title}</h2>
        </div>
        <div className="pl-8">
            {children}
        </div>
    </section>
);

// Simple subsection
const Subsection = ({ title, children }) => (
    <div className="mb-8">
        <h3 className="text-lg font-medium text-gray-800 mb-4">{title}</h3>
        <div className="space-y-4">
            {children}
        </div>
    </div>
);

// Clean list component
const CleanList = ({ items }) => (
    <ul className="space-y-2">
        {items.map((item, index) => (
            <li key={index} className="flex items-start space-x-3">
                <div className="w-1 h-1 bg-gray-400 rounded-full mt-3 flex-shrink-0" />
                <span className="text-gray-700 leading-relaxed text-sm">{item}</span>
            </li>
        ))}
    </ul>
);

// Minimal highlight box
const HighlightBox = ({ children }) => (
    <div className="border-l-2 border-gray-300 pl-4 py-2 bg-gray-50/50">
        <div className="text-sm text-gray-700 leading-relaxed">{children}</div>
    </div>
);

const PrivacyPolicy = () => {
    return (
        <div className="min-h-screen relative">
            <Background />

            <div className="relative z-10 max-w-3xl mx-auto px-8 py-16">
                <Header />

                {/* Introduction */}
                <div className="mb-16 pb-8 border-b border-gray-200">
                    <p className="text-gray-700 leading-relaxed">
                        We appreciate your interest and trust in the <strong>"Himnario LLDM"</strong> application.
                        The privacy and security of your data are of utmost importance to us. This Privacy Policy
                        aims to provide you with clear information about the handling of data when you use our application.
                    </p>
                </div>

                {/* Section 1: Data Collection */}
                <Section icon={User} title="1. Information Collection">
                    <Subsection title="1.1. Personal Information">
                        <p className="text-gray-700 leading-relaxed mb-4 text-sm">
                            The "Himnario LLDM" application does not directly collect or store any personally identifiable
                            information from users. However, our application uses Google services that may collect certain information:
                        </p>
                        <CleanList items={[
                            "Google Play Billing: Handles the information necessary to process in-app purchases",
                            "Firebase Cloud Messaging: Manages device tokens for push notifications",
                            "Google AdMob: Manages advertising identifiers and ad preferences",
                            "Firebase Analytics: Collects anonymous usage data"
                        ]} />
                        <HighlightBox>
                            All personal information is handled directly by these Google services under their own
                            privacy policies and security standards.
                        </HighlightBox>
                    </Subsection>

                    <Subsection title="1.2. Usage Information">
                        <p className="text-gray-700 leading-relaxed mb-4 text-sm">
                            The Google services integrated into our application may collect:
                        </p>
                        <CleanList items={[
                            "Anonymous feature usage statistics",
                            "Application performance data",
                            "Anonymous navigation patterns",
                            "Error information",
                            "Session metrics"
                        ]} />
                        <p className="text-gray-600 text-xs mt-4">
                            This information is completely anonymous and is processed by Firebase Analytics and Google Analytics.
                        </p>
                    </Subsection>
                </Section>

                {/* Section 2: Data Usage */}
                <Section icon={Eye} title="2. Use of Information">
                    <Subsection title="2.1. Use by Google Services">
                        <p className="text-gray-700 leading-relaxed mb-4 text-sm">
                            Google services use the collected information to:
                        </p>
                        <CleanList items={[
                            "Firebase: Provide anonymous usage and behavior analytics",
                            "AdMob: Manage relevant advertising",
                            "Play Billing: Process purchase transactions",
                            "Firebase Messaging: Manage notification delivery"
                        ]} />
                    </Subsection>

                    <Subsection title="2.2. Internal Use">
                        <p className="text-gray-700 leading-relaxed mb-4 text-sm">
                            We only access aggregated and anonymous data to:
                        </p>
                        <CleanList items={[
                            "Improve the application's functionality",
                            "Optimize the user experience",
                            "Analyze overall performance",
                            "Make decisions about future updates"
                        ]} />
                    </Subsection>
                </Section>

                {/* Section 3: Advertising */}
                <Section icon={Eye} title="3. Advertising">
                    <Subsection title="3.1. Advertising Services">
                        <p className="text-gray-700 leading-relaxed mb-4 text-sm">
                            Advertising is handled exclusively by Google AdMob, which may:
                        </p>
                        <CleanList items={[
                            "Use your device's advertising identifier",
                            "Display interest-based ads",
                            "Collect usage data to optimize ads"
                        ]} />
                    </Subsection>

                    <Subsection title="3.2. Advertising Consent">
                        <p className="text-gray-700 leading-relaxed mb-4 text-sm">
                            We use Google's User Messaging Platform (UMP) to:
                        </p>
                        <CleanList items={[
                            "Manage your consent for advertising",
                            "Handle privacy preferences",
                            "Comply with privacy regulations"
                        ]} />
                    </Subsection>
                </Section>

                {/* Section 4: In-App Purchases */}
                <Section icon={Lock} title="4. In-App Purchases">
                    <Subsection title="4.1. Google Play Billing">
                        <p className="text-gray-700 leading-relaxed mb-4 text-sm">
                            All transactions are processed exclusively by Google Play Billing:
                        </p>
                        <CleanList items={[
                            "Payment information is handled directly by Google",
                            "We do not have access to your financial data",
                            "Transactions follow Google Play's security standards"
                        ]} />
                    </Subsection>
                </Section>

                {/* Section 5: Security */}
                <Section icon={Shield} title="5. Security">
                    <p className="text-gray-700 leading-relaxed mb-4 text-sm">
                        Data security is ensured by:
                    </p>
                    <CleanList items={[
                        "Google's security protocols for its services",
                        "Secure connections with Firebase services",
                        "Google Play security standards"
                    ]} />
                </Section>

                {/* Section 6: Rights */}
                <Section icon={CheckCircle} title="6. Your Rights and Options">
                    <p className="text-gray-700 leading-relaxed mb-4 text-sm">You may:</p>
                    <CleanList items={[
                        "Manage your advertising preferences in your device settings",
                        "Control notification permissions",
                        "Manage your data through your Google account",
                        "Disable analytics data collection when this option is available"
                    ]} />
                </Section>

                {/* Section 7: Policy Changes */}
                <Section icon={FileText} title="7. Policy Changes">
                    <p className="text-gray-700 leading-relaxed mb-4 text-sm">
                        Changes to this policy will be notified through:
                    </p>
                    <CleanList items={[
                        "Application updates",
                        "In-app messages (managed by Firebase)",
                        "The application's user interface"
                    ]} />
                </Section>

                {/* Section 8: Contact */}
                <Section icon={Phone} title="8. Contact">
                    <p className="text-gray-700 leading-relaxed mb-4 text-sm">
                        For any questions about this policy, you may:
                    </p>
                    <CleanList items={[
                        "Use the contact section in the application",
                        "Contact us through the Google Play Store",
                        "Use the contact options available in the app"
                    ]} />
                </Section>

                {/* Section 9: Consent */}
                <Section icon={CheckCircle} title="9. Consent">
                    <p className="text-gray-700 leading-relaxed mb-4 text-sm">
                        By using the application, you accept:
                    </p>
                    <CleanList items={[
                        "The use of Google services for analytics and advertising",
                        "The processing of purchases through Google Play",
                        "The management of notifications by Firebase (optional)"
                    ]} />

                    <HighlightBox>
                        <strong>Note:</strong> All personal information is handled exclusively by the aforementioned Google services,
                        following their privacy and security standards. The "Himnario LLDM" application
                        does not directly store or process any personally identifiable information.
                    </HighlightBox>
                </Section>

                {/* Footer */}
                <footer className="text-center mt-16 pt-8 border-t border-gray-200">
                    <p className="text-gray-600 text-xs">
                        This privacy policy is designed to be transparent and easy to understand.
                        If you have any questions, do not hesitate to contact us.
                    </p>
                </footer>
            </div>
        </div>
    );
};

export default PrivacyPolicy;